import {
  creatorLabel,
  creatorsOf,
  FILE_DATE_FILTERS,
  folderTitle,
  typesOf,
  workspacesOf,
  type BrowsableFile,
  type FileCreator,
  type FileFilterState,
  activeFileFilterCount,
  NO_FILE_FILTERS,
} from '../../../lib/file-browser';
import { useAgentName } from '../../../lib/use-agent-name';
import { useMyAgents } from '../../../lib/my-agents';
import { useTranslation } from '../../../lib/i18n';
import { OverflowMenu, ScrollArea, type OverflowMenuAction } from '../ui';
import { ClearFiltersButton } from '../category-view/CategoryView';
import { CategoryFilterMenu, useSpaceMark } from '../category-view/CategoryFilterMenu';
import { FileCreatorMark } from '../FileCreatedBy';
import styles from './FilesFilterBar.module.css';

// The Files list's filter row: **Space, then Created by, then Date, then Type** — each a labelled DS picker
// whose label states the current value, plus a Clear that appears once something is narrowed.
//
// It is the row Tasks and Routines already ship, with the two axes only files have added to it. Same chips,
// same reading — a picker's rest label states the whole SET it is offering ("All Spaces", "All agents",
// "Any type") rather than naming the bare dimension, so the row reads as an answer instead of a set of
// prompts. Three lists that ask the same question of the same account should not each phrase it differently.
//
// The Space and Created-by rest labels are the very keys Tasks and Routines use (`filter.allSpaces` /
// `filter.allAgents`, already translated in every bundle); Date and Type keep their own "Any …" wording,
// which says the same thing and is likewise already translated.
//
// **Space leads, and it is the one axis that MOVES you**: picking a Space makes it the browsing root
// (`browseRoot`), which is what lets the cross-workspace screen open flat instead of on a row of workspace
// folders. Created by comes next because it is the question the files themselves are most often narrowed by.
//
// **There is no Owner filter.** It used to be a TAB pair ("Uploaded Files" / "Agent Files"), then briefly a
// fourth picker, and it is gone: created-by supersedes it. Picking yourself under Created by asks the same
// question against the same data, and a row offering both invites the state where the two disagree.
//
// The Space, Type and Created-by option lists are DERIVED FROM THE FILES IN SCOPE (`workspacesOf` / `typesOf` / `creatorsOf`), so a
// menu can never offer a value that matches nothing — a fixed roster of agents goes stale the moment a new
// actor type writes a file. Each control is omitted entirely when there is nothing to choose between.
export function FilesFilterBar({
  items,
  workspaceItems,
  workspaceNames = {},
  filters,
  scopeLabel,
  onChange,
  onBi,
  className,
  axes = 'all',
}: {
  /** The files the options are derived from — everything beneath the folder being browsed. */
  items: BrowsableFile[];
  /** The files the SPACE options are derived from — the whole surface, not the current Space's subtree, or
   *  picking a Space would leave its own menu with one entry and hide the way back out. */
  workspaceItems?: BrowsableFile[];
  /** Workspace id → display name. A Space whose name is unresolved is not offered. */
  workspaceNames?: Record<string, string>;
  filters: FileFilterState;
  /** What the subtree-scope chip is called — the surface's own words ("Riley's files"). Without one the
   *  chip is not drawn, because an unnamed scope is a chip that cannot say what it is doing. */
  scopeLabel?: string;
  onChange: (next: FileFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
  /** Which half to draw. The category layout (Figma 21055:251370) splits them: `scope` (which Space,
   *  which agent — and the one "Clear filters · N", which clears every axis) sits in the section header,
   *  `shape` (date, type) in the toolbar band beside the search. */
  axes?: 'all' | 'scope' | 'shape';
}) {
  const { t } = useTranslation();
  const spaceMark = useSpaceMark();
  // One roster read for the creator menu: a sub-agent's write is typed by role, so its dot comes from the
  // roster agent of that name.
  const team = useMyAgents();
  const agentIdOf = (name: string) =>
    name.trim() && team.status === 'ready' ? team.team.agents.find((a) => a.name?.trim() === name.trim())?.id : undefined;
  const agent = useAgentName();
  const types = typesOf(items);
  const creators = creatorsOf(items);
  const workspaces = workspacesOf(workspaceItems ?? items, workspaceNames);

  const pick = (name: string, next: FileFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  const dateActions: OverflowMenuAction[] = FILE_DATE_FILTERS.map((bucket) => ({
    key: bucket,
    label: t(`files.filter.date.${bucket}`),
    onSelect: () => pick(`filter_date_${bucket}`, { ...filters, date: bucket }),
  }));

  const typeActions: OverflowMenuAction[] = [
    { key: 'any', label: t('files.filter.type.any'), onSelect: () => pick('filter_type_any', { ...filters, type: null }) },
    ...types.map((kind) => ({
      key: kind,
      label: t(`files.type.${kind}`),
      onSelect: () => pick(`filter_type_${kind}`, { ...filters, type: kind }),
    })),
  ];



  // A picker's label carries the current VALUE, which is the whole point of a labelled control over a glyph.
  // At rest it states the whole set ("All Spaces"), the way the Tasks and Routines rows do — a chip reading
  // the bare dimension asks a question where its neighbours give answers.
  const dateLabel = filters.date === 'any' ? t('files.filter.date.any') : t(`files.filter.date.${filters.date}`);
  const typeLabel = filters.type === null ? t('files.filter.type.any') : t(`files.type.${filters.type}`);

  // A chip that is AWAY from its rest value grows a ✕ that resets that axis and nothing else (§9). The
  // rest values are the "All …/Any …" ones.
  //
  // ⚠️ THERE IS NO "CLEAR FILTERS" BUTTON, at any width, and its absence is the design's decision rather
  // than an omission. Neither frame draws one — not the desktop header (`15623:362406`), not the phone
  // (`15891:420486`) — because every narrowed chip now carries its own ✕, so the row already says which
  // axes are active and offers the way out of each. THE TRADE: resetting everything is one tap per
  // active chip instead of a single button. That is accepted; a row of chips is at most four, and the
  // per-axis ✕ is the affordance a reader can actually see from the chip they want to undo.
  // `files.filter.clear` survives as that ✕'s accessible NAME — a glyph still has to be announced.
  const clearLabel = t('files.filter.clear');
  const showScope = axes !== 'shape';
  const showShape = axes !== 'scope';
  // A scope always names itself, whether a component supplied the words or the scope arrived in the URL
  // (`/files?folder=brain/Riley`, from an agent's Files tile). Falling back to the folder's own title
  // matters more than it looks: the chip is the ONLY way to drop the scope, so a scope with no label
  // would be a filter the reader can see the effect of and cannot turn off — the undismissable narrowing
  // this whole row exists to avoid.
  //
  // `folderTitle`, not the raw last segment: it IS the last segment for every folder someone named, and
  // the translated word for the account roots — so `?scope=knowledge` reads "Memory", the same word the
  // folder row and the breadcrumb use, rather than the directory's name in English to every locale.
  const scopeName = scopeLabel ?? folderTitle(filters.folder ?? '', workspaceNames, t);

  return (
    /* ONE row that scrolls sideways rather than wrapping onto two or three, when there is more in it than
       fits. The DS `ScrollArea` owns that overflow; a hand-rolled `overflow-x: auto` is what
       `test/scrollbar-spec.test.ts` exists to stop.

       ⚠️ A CHIP IS SIZED BY ITS LABEL, at every width. Figma's filtered chip (`15891:422056`) carries
       `whitespace-nowrap` on its label, so the chip HUGS — and on a phone the row scrolls sideways
       rather than dividing itself between the chips. Making them equal-width thirds instead collapsed
       every label to a letter and an ellipsis at 375px ("B..", "Las…", "Doc…"), which is a chip that
       has stopped saying what it filters. Reported with a screenshot. */
    <ScrollArea axis="horizontal" bar="hidden" className={className ? `${styles.bar} ${className}` : styles.bar}>
      {/* THE SUBTREE SCOPE, and it leads the row because it is the widest question asked of the list.
          It only appears when a surface applied one (an agent's folder), and it is a chip rather than a
          root precisely so it can be taken off: dismissing it drops the scope, which returns the browser
          to the whole tree in place — same screen, same breadcrumb, now showing everything.

          It carries only a CLEAR, no menu: there is no list of other subtrees to pick from, and a chip
          that opens an empty picker is worse than one that just says what it is and how to remove it. */}
      {showScope && filters.folder && (
        <OverflowMenu
          className={styles.chipWrap}
          triggerClassName={styles.chipTrigger}
          label={scopeName}
          ariaLabel={scopeName}
          actions={[]}
          onClear={() => pick('filter_scope_all', { ...filters, folder: null })}
          clearAriaLabel={clearLabel}
        />
      )}
      {/* Drawn whenever a file lives in a Space, so the row always reads "All spaces ⌄ · All agents ⌄"
          like every other category's — even when one Space holds them all. */}
      {showScope && workspaces.length > 0 && (
        <CategoryFilterMenu
          options={workspaces.map((space) => ({ id: space.id, name: space.name, icon: spaceMark(space.id, space.name) }))}
          selectedId={filters.workspaceId ?? ''}
          allLabel={t('files.filter.allSpaces')}
          ariaLabel={t('files.filter.allSpaces')}
          onPick={(id) => pick(id ? 'filter_space' : 'filter_space_all', { ...filters, workspaceId: id || null })}
        />
      )}
      {showScope && creators.length > 0 && (
        <CategoryFilterMenu
          options={creators.map((creator) => ({
            id: creatorKey(creator),
            name: creatorLabel(creator, t, agent.short),
            icon: <FileCreatorMark creator={creator} size={16} agentId={agentIdOf(creator.name)} />,
          }))}
          selectedId={filters.creator ? creatorKey(filters.creator) : ''}
          allLabel={t('files.filter.allAgents')}
          ariaLabel={t('files.createdBy')}
          onPick={(id) =>
            pick(id ? 'filter_creator' : 'filter_creator_any', { ...filters, creator: creators.find((creator) => creatorKey(creator) === id) ?? null })
          }
        />
      )}
      {showShape && (
        <OverflowMenu
          triggerVariant="text"
          triggerSize="small"
          label={dateLabel}
          ariaLabel={t('agentCreations.table.date')}
          actions={dateActions}
          selectedKey={filters.date}
        />
      )}
      {/* Always drawn, like the date filter (Figma 21055:251370): with one type in the list it still names
          what the list holds, and its menu still offers it. */}
      {showShape && (
        <OverflowMenu
          triggerVariant="text"
          triggerSize="small"
          label={typeLabel}
          ariaLabel={t('agentCreations.detail.type')}
          actions={typeActions}
          selectedKey={filters.type ?? 'any'}
        />
      )}
      {showScope && (
        <ClearFiltersButton count={activeFileFilterCount(filters)} onClear={() => pick('filter_clear', NO_FILE_FILTERS)} />
      )}
    </ScrollArea>
  );
}

/** A creator's menu key. The PAIR identifies a writer (see `FileCreator`), so both halves are in the key —
 *  otherwise two same-typed writers with different names would share one check mark. */
function creatorKey(creator: FileCreator): string {
  return `${creator.type} ${creator.name}`;
}
